import css from './APIManagement.module.css';
import { useEffect, useState, useContext, useMemo } from 'react';

import { KeyListItem } from '../../../types/appModels';
import { ErrorCode } from '../../../types/appTypes';
import {
  AdminDataTable,
  BooleanDataColumn,
  DataTableRow,
  TextDataColumn,
  SortColumn,
  SortDirection,
  FilterColumn,
  OptionsDataFilter,
  ActionsColumn,
  TableStatus,
} from '../AdminDataTable/AdminDataTable';
import { useTableData } from '../../../hooks/useTableData';
import { UserContext } from '../../../context/userContext';
import { useNavigate } from 'react-router-dom';
import { 
  FiEdit2,
  FiCheckCircle,
  FiSlash,
  FiTrash2
} from 'react-icons/fi';
import ConfirmationModal from '../../Common/ConfirmationModal/ConfirmationModal';

interface KeyDataListItem extends DataTableRow {
  profileId: string;
  name: string;
  email: string;
  companies: string;
  apiType: string;
  pads: string;
  enabled: boolean;
}

const APIManagement = () => {
  const { user } = useContext(UserContext);
  const nav = useNavigate();
  const { dataState, setSort, setFilters, startAction, actionFailed, reload } =
    useTableData<KeyListItem>({
      baseEndpoint: process.env.REACT_APP_API_KEYS!,
      initialData: { count: 0, list: [] },
      defaultSort: {
        columnId: 'name',
        direction: SortDirection.Ascending,
      },
    });

  const [isMobile, setIsMobile] = useState(false);
  const [showModal, setShowModal] = useState<{
    type: 'enable' | 'disable' | 'delete' | null;
    row: KeyDataListItem | null;
  }>({ type: null, row: null });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const columns = useMemo(() => {
    const cols = [
      // Only include the Name column if not mobile
      ...(!isMobile ? [
        new TextDataColumn<KeyDataListItem>('name', 'Name', true, true, 25,
          (value) => (
            <div className={css.textCell}>{value}</div>
          )
        ),
      ] : []),
      new TextDataColumn<KeyDataListItem>('email', 'Email', true, true, 25,
        (value) => (
          <div className={css.textCell}>{value}</div>
        )
      ),
      new TextDataColumn<KeyDataListItem>(
        'companies',
        isMobile ? 'Comp' : 'Companies',
        true,
        true,
        25,
        (value) => (
          <div className={css.textCell}>{value}</div>
        )
      ),
      new TextDataColumn<KeyDataListItem>(
        'apiType',
        isMobile ? 'Type' : 'Access Type',
        true,
        true,
        25,
        (value) => (
          <div className={css.textCell}>{value}</div>
        ),
        undefined,
        undefined,
        (column, handleFilter) => {
          return (
            <OptionsDataFilter
              column={column}
              handleFilter={handleFilter}
              options={[
                { label: isMobile ? 'Comp' : 'Company', value: 'Company' },
                { label: 'Pad', value: 'Pad' },
              ]}
            />
          );
        }
      ),
      new TextDataColumn<KeyDataListItem>('pads', 'Pads', true, true, 25,
        (value) => (
          <div className={css.textCell}>{value}</div>
        )
      ),
      new BooleanDataColumn<KeyDataListItem>(
        'enabled', 
        'Enabled', 
        true, 
        true, 
        25,
        (value) => (
          <span className={`${css.statusBadge} ${value ? css.statusActive : css.statusInactive}`}>
            {value ? 'Active' : 'Inactive'}
          </span>
        )
      ),
      new ActionsColumn<KeyDataListItem>(
        'actions', 
        'Actions', 
        [
          {
            action: 'MANAGE',
            label: 'manage',
            icon: <FiEdit2 />,
          },
          {
            action: 'ENABLE',
            label: 'enable',
            allowed: (r) => !r.enabled,
            icon: <FiCheckCircle />,
          },
          {
            action: 'DISABLE',
            label: 'disable',
            allowed: (r) => r.enabled,
            icon: <FiSlash />,
          },
          {
            action: 'DELETE',
            label: 'delete',
            icon: <FiTrash2 />,
          },
        ],
        true,
        25
      ),
    ];
    return cols;
  }, [isMobile]);

  let rows: KeyDataListItem[] = [];
  let count: number = 0;
  if (dataState.data !== undefined) {
    rows = dataState.data!.list.map<KeyDataListItem>((item) => {
      return {
        id: item.keyId,
        profileId: item.profileId,
        name: item.name,
        email: item.email,
        companies: item.companies.join(', '),
        apiType: item.apiType,
        pads: item.apiType === 'Pad' ? item.apiData.join(', ') : '',
        enabled: item.enabled,
      };
    });

    // sort data
    if (
      dataState.sort !== undefined &&
      dataState.sort.direction !== SortDirection.None
    ) {
      rows.sort((a, b) => {
        const vA = a[dataState.sort!.columnId];
        const vB = b[dataState.sort!.columnId];
        if (vA === vB) return 0;
        if (vA === undefined) return -1;
        if (vB === undefined) return 1;
        return vA < vB ? -1 : 1;
      });

      if (dataState.sort.direction === SortDirection.Descending) rows.reverse();
    }

    // filter data
    if (dataState.filters !== undefined && dataState.filters.length > 0) {
      for (const filter of dataState.filters) {
        if (filter.filter === undefined) continue;

        if (filter.columnId.endsWith('From')) {
          const fc = filter.columnId.replace('From', '');
          rows = rows.filter(
            (r) => r[fc]! >= new Date(Date.parse(filter.filter!))
          );
        } else if (filter.columnId.endsWith('To')) {
          const fc = filter.columnId.replace('To', '');
          rows = rows.filter(
            (r) => r[fc]! <= new Date(Date.parse(filter.filter!))
          );
        } else if (filter.columnId === 'enabled') {
          const fv = filter.filter === 'true';
          rows = rows.filter((r) => r['enabled'] === fv);
        } else if (filter.columnId === 'companies') {
          rows = rows.filter((r) => r['companies'].includes(filter.filter!));
        } else {
          rows = rows.filter((r) => r[filter.columnId] === filter.filter);
        }
      }
    }

    count = rows.length;
  }

  const handleSort = (sc: SortColumn | undefined) => {
    setSort(sc);
  };

  const handleFilter = (filters: FilterColumn[]) => {
    setFilters(filters);
  };

  const handleAdd = () => {
    nav(`/admin/createKey`);
  };

  const handleAction = (action: string, row: KeyDataListItem) => {
    if (action === 'MANAGE') {
      handleManageAction(row);
    } else if (action === 'ENABLE') {
      handleEnableAction(row);
    } else if (action === 'DISABLE') {
      handleDisableAction(row);
    } else if (action === 'DELETE') {
      handleDeleteAction(row);
    }
  };

  const handleManageAction = (row: KeyDataListItem) => {
    nav(`/admin/manageKey?did=${row.id}&pid=${row.profileId}`);
  };

  const handleEnableAction = (row: KeyDataListItem) => {
    setShowModal({ type: 'enable', row });
  };

  const handleDisableAction = (row: KeyDataListItem) => {
    setShowModal({ type: 'disable', row });
  };

  const handleDeleteAction = (row: KeyDataListItem) => {
    setShowModal({ type: 'delete', row });
  };

  const handleConfirmAction = async () => {
    if (!showModal.row) return;

    const endpoint = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEYS!}/${showModal.row.id}`;
    let method = 'PUT';
    let actionEndpoint = '';

    switch (showModal.type) {
      case 'enable':
        actionEndpoint = '/enable';
        break;
      case 'disable':
        actionEndpoint = '/disable';
        break;
      case 'delete':
        method = 'DELETE';
        break;
      default:
        return;
    }

    startAction();
    try {
      const response = await fetch(`${endpoint}${actionEndpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + user!.accessToken,
        },
      });

      if (response.status === 204) {
        reload();
      } else if (response.status === 404) {
        actionFailed(TableStatus.Fail, {
          code: ErrorCode.notFound,
          message: `The key could not be found or has already been ${showModal.type}d.`,
        });
      } else {
        actionFailed(TableStatus.Fail, {
          code: ErrorCode.unknown,
          message: `An error occurred and the key could not be ${showModal.type}d.`,
        });
      }
    } catch (err) {
      actionFailed(TableStatus.Error, {
        code: ErrorCode.unknown,
        message: `An error occurred and the key could not be ${showModal.type}d.`,
      });
    } finally {
      setShowModal({ type: null, row: null });
    }
  };

  const getModalConfig = () => {
    if (!showModal.type || !showModal.row) return null;

    const configs = {
      enable: {
        title: 'Enable Key',
        message: 'Are you sure you want to enable this key?',
        confirmText: 'Enable',
      },
      disable: {
        title: 'Disable Key',
        message: 'Are you sure you want to disable this key?',
        confirmText: 'Disable',
      },
      delete: {
        title: 'Delete Key',
        message: 'Are you sure you want to delete this key?',
        confirmText: 'Delete',
        icon: <FiTrash2 />
      },
    };

    return configs[showModal.type];
  };

  // Custom actions renderer
  const renderActionIcons = (row: KeyDataListItem) => (
    <div className={css.actionsContainer}>
      <FiEdit2 
        className={`${css.actionButton} ${css.manageButton}`} 
        onClick={() => handleManageAction(row)}
        title="Manage key"
      />
      
      {!row.enabled && (
        <FiCheckCircle 
          className={`${css.actionButton} ${css.enableButton}`} 
          onClick={() => handleEnableAction(row)}
          title="Enable key"
        />
      )}
      
      {row.enabled && (
        <FiSlash 
          className={`${css.actionButton} ${css.disableButton}`} 
          onClick={() => handleDisableAction(row)}
          title="Disable key"
        />
      )}
      
      <FiTrash2 
        className={`${css.actionButton} ${css.deleteButton}`} 
        onClick={() => handleDeleteAction(row)}
        title="Delete key"
      />
    </div>
  );

  return (
    <div className={css.apiManagementContainer}>
      <AdminDataTable
        columns={columns}
        rows={rows}
        total={count}
        allowFilter={true}
        allowNoSort={false}
        allowAdd={true}
        addLabel='Create Key'
        onSort={handleSort}
        onFilter={handleFilter}
        onAdd={handleAdd}
        onAction={handleAction}
        state={dataState}
        renderActionIcons={renderActionIcons}
      />

      {showModal.type && showModal.row && (
        <ConfirmationModal
          {...getModalConfig()!}
          onConfirm={handleConfirmAction}
          onCancel={() => setShowModal({ type: null, row: null })}
        />
      )}
    </div>
  );
};

export default APIManagement;
